"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.queryParams = void 0;
const index_js_1 = require("../../../../attributes/any/index.js");
const index_js_2 = require("../../../../entity/actions/parseCondition/index.js");
const index_js_3 = require("../../../../entity/actions/parsePaths/index.js");
const index_js_4 = require("../../../../errors/index.js");
const capacity_js_1 = require("../../../../options/capacity.js");
const consistent_js_1 = require("../../../../options/consistent.js");
const index_js_5 = require("../../../../options/index.js");
const limit_js_1 = require("../../../../options/limit.js");
const maxPages_js_1 = require("../../../../options/maxPages.js");
const rejectExtraOptions_js_1 = require("../../../../options/rejectExtraOptions.js");
const select_js_1 = require("../../../../options/select.js");
const tableName_js_1 = require("../../../../options/tableName.js");
const index_js_6 = require("../../../../schema/actions/parseCondition/index.js");
const isEmpty_js_1 = require("../../../../utils/isEmpty.js");
const isBoolean_js_1 = require("../../../../utils/validation/isBoolean.js");
const parseQuery_js_1 = require("./parseQuery.js");
const defaultAnyAttribute = new index_js_1.AnyAttribute({
    required: 'never',
    hidden: false,
    key: false,
    savedAs: undefined,
    defaults: { key: undefined, put: undefined, update: undefined },
    links: { key: undefined, put: undefined, update: undefined },
    validators: { key: undefined, put: undefined, update: undefined },
    castAs: undefined
});
const queryParams = (table, entities = [], query, options = {}) => {
    const { index } = query;
    const { capacity, consistent, exclusiveStartKey, limit, maxPages, reverse, select, entityAttrFilter = true, filter, filters: _filters, attributes: _attributes, tableName, ...extraOptions } = options;
    (0, rejectExtraOptions_js_1.rejectExtraOptions)(extraOptions);
    const filters = (_filters !== null && _filters !== void 0 ? _filters : {});
    const attributes = _attributes;
    if (tableName !== undefined) {
        (0, tableName_js_1.parseTableNameOption)(tableName);
    }
    const commandOptions = {
        TableName: tableName !== null && tableName !== void 0 ? tableName : table.getName()
    };
    if (capacity !== undefined) {
        commandOptions.ReturnConsumedCapacity = (0, capacity_js_1.parseCapacityOption)(capacity);
    }
    if (consistent !== undefined) {
        commandOptions.ConsistentRead = (0, consistent_js_1.parseConsistentOption)(consistent, index);
    }
    if (exclusiveStartKey !== undefined) {
        commandOptions.ExclusiveStartKey = exclusiveStartKey;
    }
    if (index !== undefined) {
        commandOptions.IndexName = (0, index_js_5.parseIndexOption)(table, index);
    }
    if (limit !== undefined) {
        commandOptions.Limit = (0, limit_js_1.parseLimitOption)(limit);
    }
    if (maxPages !== undefined) {
        // maxPages is a meta-option, validated but not used here
        (0, maxPages_js_1.parseMaxPagesOption)(maxPages);
    }
    if (reverse !== undefined) {
        if (!(0, isBoolean_js_1.isBoolean)(reverse)) {
            throw new index_js_4.DynamoDBToolboxError('queryCommand.invalidReverseOption', {
                message: 'Invalid "reverse" options: Must be a boolean',
                payload: { reverse }
            });
        }
        commandOptions.ScanIndexForward = !reverse;
    }
    if (select !== undefined) {
        commandOptions.Select = (0, select_js_1.parseSelectOption)(select, { index, attributes });
    }
    if (!(0, isBoolean_js_1.isBoolean)(entityAttrFilter)) {
        // entityAttrFilter is a meta-option, validated but not used here
        throw new index_js_4.DynamoDBToolboxError('queryCommand.invalidEntityAttrFilterOption', {
            message: 'Invalid "entityAttrFilter" options: Must be a boolean',
            payload: { entityAttrFilter }
        });
    }
    const expressionAttributeNames = {};
    const expressionAttributeValues = {};
    const { KeyConditionExpression, ExpressionAttributeNames: keyConditionExpressionAttributeNames, ExpressionAttributeValues: keyConditionExpressionAttributeValues } = (0, parseQuery_js_1.parseQuery)(table, query);
    commandOptions.KeyConditionExpression = KeyConditionExpression;
    Object.assign(expressionAttributeNames, keyConditionExpressionAttributeNames);
    Object.assign(expressionAttributeValues, keyConditionExpressionAttributeValues);
    if (entities.length === 0 && filter !== undefined) {
        const { ExpressionAttributeNames: filterExpressionAttributeNames, ExpressionAttributeValues: filterExpressionAttributeValues, ConditionExpression: filterExpression } = new index_js_6.ConditionParser(defaultAnyAttribute).setId('1').parse(filter).toCommandOptions();
        Object.assign(expressionAttributeNames, filterExpressionAttributeNames);
        Object.assign(expressionAttributeValues, filterExpressionAttributeValues);
        commandOptions.FilterExpression = filterExpression;
    }
    if (entities.length > 0) {
        const filterExpressions = [];
        let projectionExpression = undefined;
        let index = 0;
        for (const entity of entities) {
            /**
             * @debt feature "For now, we compute the projectionExpression using the first entity. Will probably use Table schemas once they exist."
             */
            if (projectionExpression === undefined && attributes !== undefined) {
                const { entityAttributeName } = entity;
                const { ExpressionAttributeNames: projectionExpressionAttributeNames, ProjectionExpression } = entity
                    .build(index_js_3.EntityPathParser)
                    .parse(
                // entityAttributeName is required at all times for formatting
                attributes.includes(entityAttributeName)
                    ? attributes
                    : [entityAttributeName, ...attributes])
                    .toCommandOptions();
                Object.assign(expressionAttributeNames, projectionExpressionAttributeNames);
                projectionExpression = ProjectionExpression;
            }
            const entityFilter = filters[entity.name];
            if (entityFilter === undefined && !entityAttrFilter) {
                continue;
            }
            const entityNameFilter = { attr: entity.entityAttributeName, eq: entity.name };
            const { ExpressionAttributeNames: filterExpressionAttributeNames, ExpressionAttributeValues: filterExpressionAttributeValues, ConditionExpression: filterExpression } = entity
                .build(index_js_2.EntityConditionParser)
                // Need to add +1 to take KeyConditionExpression into account
                .setId((index + 1).toString())
                .parse(entityFilter !== undefined ? { and: [entityNameFilter, entityFilter] } : entityNameFilter)
                .toCommandOptions();
            Object.assign(expressionAttributeNames, filterExpressionAttributeNames);
            Object.assign(expressionAttributeValues, filterExpressionAttributeValues);
            filterExpressions.push(filterExpression);
            index++;
        }
        if (filterExpressions.length > 0) {
            commandOptions.FilterExpression =
                filterExpressions.length === 1
                    ? filterExpressions[0]
                    : `(${filterExpressions.filter(Boolean).join(') OR (')})`;
        }
        if (projectionExpression !== undefined) {
            commandOptions.ProjectionExpression = projectionExpression;
        }
    }
    if (!(0, isEmpty_js_1.isEmpty)(expressionAttributeNames)) {
        commandOptions.ExpressionAttributeNames = expressionAttributeNames;
    }
    if (!(0, isEmpty_js_1.isEmpty)(expressionAttributeValues)) {
        commandOptions.ExpressionAttributeValues = expressionAttributeValues;
    }
    return commandOptions;
};
exports.queryParams = queryParams;
